import { useEffect } from 'react';
import { toast } from 'sonner';
import { setItem as lsSet } from '../utils/storage';

/**
 * CommandPaletteOnboardingHint
 *
 * Fires a one-time toast nudging users toward the Cmd+K command palette.
 * Most users never discover it without a hint, and the palette is now the
 * fastest path to the audit/push/blocked/emergency features.
 *
 * Persistence: we write `ghostcomm_cmdk_hinted=1` via the metadata-wrapped
 * storage helper so (a) the Local Data Inspector shows when the hint was
 * dismissed and (b) the panic button wipes it along with every other
 * `ghostcomm_*` key, which re-arms the hint on the next sign-in.
 *
 * Rendered as a hook-only component (returns null) so placement is free.
 */
const LS_KEY = 'ghostcomm_cmdk_hinted';
// Mac uses ⌘, every other platform uses Ctrl. Detect best-effort.
const MOD = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform)
  ? '⌘K'
  : 'Ctrl+K';

export const CommandPaletteOnboardingHint = ({ onOpenPalette }) => {
  useEffect(() => {
    try {
      if (window.localStorage.getItem(LS_KEY) === '1') return;
    } catch {
      return;
    }
    // Give the dashboard a beat to paint so the toast doesn't collide with
    // login-landing toasts.
    const t = setTimeout(() => {
      toast(
        `Tip: press ${MOD} to jump anywhere — audit, push, blocked, emergency, contacts…`,
        {
          duration: 10000,
          action: {
            label: 'Show me',
            onClick: () => onOpenPalette?.(),
          },
          onDismiss: () => {
            try { lsSet(LS_KEY, '1'); } catch {}
          },
          onAutoClose: () => {
            try { lsSet(LS_KEY, '1'); } catch {}
          },
        }
      );
      // Mark shown immediately so a second mount (fast-refresh / navigation)
      // doesn't re-fire. The dismiss/autoClose handlers above are belt-&-
      // braces for environments where sonner doesn't invoke them.
      try { lsSet(LS_KEY, '1'); } catch {}
    }, 2500);
    return () => clearTimeout(t);
  }, [onOpenPalette]);
  return null;
};

export default CommandPaletteOnboardingHint;
